import constants from '../../../Config/constants';
import Service from '../../../Service';

import { baseApi, METHODS } from '../baseApi';
import { serializeParams } from '../helpers';
import {
  ApiArgs,
  AvailableRange,
  AvailableRangeMap,
  Checkout,
  PaginatedResult,
  Validate,
} from '../types';

import {
  AvailableRangePayload,
  CheckoutReservationPayload,
  GetListingPayload,
  GetTablesPayload,
  Listing,
  TableAvailabilityPayload,
  TableDetails,
  TableReservableDays,
  ValidatePayload,
} from './types';

export const RESERVATION_TAGS = {
  AVAILABLE_RANGE: 'available_by_range',
};

export const reservationApi = baseApi
  .enhanceEndpoints({ addTagTypes: Object.values(RESERVATION_TAGS) })
  .injectEndpoints({
    endpoints: (builder) => ({
      getListing: builder.query<
        PaginatedResult<Listing>,
        ApiArgs<void, GetListingPayload>
      >({
        query: (params) => {
          const { type, ...payload } = params.payload;
          const routes = {
            [constants.RESERVATION_TYPE.EVENT]: '/reservation/events',
            [constants.RESERVATION_TYPE.DINING]: '/reservation/tables',
          };
          const path = routes[type] || '/reservation';
          return {
            url: `${path}${serializeParams(payload)}`,
            method: 'GET',
            headers: { 'x-api-version': '1' },
          };
        },
        transformErrorResponse: (error: any) => {
          console.log('error', JSON.stringify(error, null, 2));
          return Service.handleErrorMessage(error?.data?.message);
        },
      }),
      getTables: builder.query<TableDetails[], ApiArgs<void, GetTablesPayload>>(
        {
          query: (params) => {
            const { store_id, ...payload } = params.payload;
            return {
              url: `/reservation/${store_id}/tables`,
              method: 'GET',
              headers: { 'x-api-version': '1' },
              params: payload,
            };
          },
          transformErrorResponse: (error: any) =>
            Service.handleErrorMessage(error?.data?.message),
        },
      ),
      validate: builder.query<Validate, ApiArgs<void, ValidatePayload>>({
        query: (params) => {
          const { id, type, ...payload } = params.payload;
          const routes = {
            [constants.RESERVATION_TYPE
              .EVENT]: `/reservation/events/${id}/available`,
            [constants.RESERVATION_TYPE
              .DINING]: `/reservation/tables/${id}/available`,
          };
          return {
            url: routes[type],
            method: 'GET',
            headers: { 'x-api-version': '1' },
            params: payload,
          };
        },
      }),
      getAvailableByRange: builder.query<
        AvailableRangeMap,
        ApiArgs<void, AvailableRangePayload>
      >({
        providesTags: [RESERVATION_TAGS.AVAILABLE_RANGE],
        query: (params) => {
          const { id, ...payload } = params.payload;
          return {
            url: `/reservation/tables/${id}/available/range`,
            method: 'GET',
            headers: { 'x-api-version': '1' },
            params: payload,
          };
        },
        transformResponse: (data: AvailableRange[], _meta) => {
          return data.reduce((acc, available) => {
            acc[available.date] = available;
            return acc;
          }, {} as AvailableRangeMap);
        },
      }),
      getTableAvailability: builder.query<
        TableDetails,
        ApiArgs<void, TableAvailabilityPayload>
      >({
        query: (params) => {
          const { id, ...payload } = params.payload;
          return {
            url: `/reservation/tables/${id}/time_slots`,
            method: 'GET',
            headers: { 'x-api-version': '1' },
            params: payload,
          };
        },
      }),
      getTableReservableDays: builder.query<
        TableReservableDays,
        ApiArgs<void, string>
      >({
        query: (params) => ({
          url: `reservation/${params.payload}/table-reservable-days`,
          method: 'GET',
          headers: { 'x-api-version': '1' },
        }),
      }),
      checkoutReservation: builder.mutation<
        Checkout,
        ApiArgs<void, CheckoutReservationPayload>
      >({
        invalidatesTags: [RESERVATION_TAGS.AVAILABLE_RANGE],
        query: (params) => ({
          url: '/reservation',
          method: METHODS.POST,
          body: params.payload,
          headers: { 'x-api-version': '1' },
        }),
        transformErrorResponse: (result) => {
          const errData = result.data as any;
          return Service.handleErrorMessage(errData.message) as string;
        },
      }),
    }),
    overrideExisting: true,
  });

export const {
  useGetListingQuery,
  useLazyGetListingQuery,
  useGetTablesQuery,
  useLazyValidateQuery,
  useGetAvailableByRangeQuery,
  useGetTableAvailabilityQuery,
  useGetTableReservableDaysQuery,
  useCheckoutReservationMutation,
} = reservationApi;
